import React, { useState, useEffect, useRef } from 'react';
import logo from "../icon/logo.png";

/* ─────────────────────────────────────────────
   DESIGN TOKENS — "Digitalbrutalnft // THE RESERVE"
   Matches login/signup/about/disclosures/policy/landing.
───────────────────────────────────────────── */
const C = {
  bg:         "#000000",
  surface:    "#0A0A0A",
  surfaceAlt: "#121212",
  border:     "#1F1F1F",
  borderHover:"#333333",
  accent:     "#C9A15C",
  accentDim:  "rgba(201,161,92,0.10)",
  accentGlow: "rgba(201,161,92,0.30)",
  text:       "#EDEDEF",
  textMid:    "#9A9AA0",
  textDim:    "#5C5C62",
  red:        "#C9695F",
  green:      "#6FA383",
  blue:       "#8C99A8",
  purple:     "#9C87B8",
  amber:      "#C9884B",
};

/* ─────────────────────────────────────────────
   TERMS CONTENT
   NOTE: all inner quotes use \" or single '
───────────────────────────────────────────── */
const SECTIONS = [
  {
    id: "acceptance",
    icon: "✅",
    color: C.accent,
    title: "Acceptance of Terms",
    content: [
      { type:"p", text:"By accessing or using the Digitalbrutalnft platform, website, or any associated services (collectively, the \"Services\"), you agree to be bound by these Terms of Service (\"Terms\"). If you do not agree, you must not use the Services." },
      { type:"p", text:"These Terms constitute a legally binding agreement between you and Digitalbrutalnft Technologies Ltd, a company registered in England and Wales (Company No. 12345678), whose registered office is at 1 Canada Square, London E14 5AB." },
      { type:"callout", icon:"📋", text:"Last updated: 1 May 2025. We may revise these Terms at any time. Continued use of the Services after changes take effect constitutes your acceptance of the revised Terms." },
    ],
  },
  {
    id: "eligibility",
    icon: "🪪",
    color: C.blue,
    title: "Eligibility",
    content: [
      { type:"p", text:"To use Digitalbrutalnft you must meet all of the following criteria at all times:" },
      { type:"list", label:"Requirements", items:[
        "Be at least 18 years of age (or the age of majority in your jurisdiction, if higher)",
        "Have the legal capacity to enter into a binding contract",
        "Not be a resident of a jurisdiction where our Services are prohibited or restricted",
        "Not be subject to any sanctions lists maintained by the UK, EU, UN, or US authorities",
        "Have completed our identity verification (KYC) process successfully",
      ]},
      { type:"callout", icon:"⚠️", text:"Digitalbrutalnft is not available to residents of the United States, North Korea, Iran, or any other jurisdiction where financial services are restricted. It is your responsibility to ensure compliance with local laws." },
    ],
  },
  {
    id: "account",
    icon: "👤",
    color: C.purple,
    title: "Your Account",
    content: [
      { type:"p", text:"When you create a Digitalbrutalnft account you are responsible for maintaining the confidentiality of your login credentials and for all activity that occurs under your account." },
      { type:"list", label:"Account responsibilities", items:[
        "Provide accurate, complete, and current registration information",
        "Keep your password and two-factor authentication codes secure",
        "Notify us immediately at security@Digitalbrutalnft.io if you suspect unauthorised access",
        "Not share your account with, or transfer it to, any third party",
        "Not create more than one account per person without prior written consent",
      ]},
      { type:"p", text:"We reserve the right to suspend or terminate accounts that violate these Terms, provide false information, or engage in activity that threatens the security or integrity of the platform." },
    ],
  },
  {
    id: "services",
    icon: "⚙️",
    color: C.accent,
    title: "Services & Features",
    content: [
      { type:"p", text:"Digitalbrutalnft provides a technology platform that enables users to copy the trading strategies of selected signal providers, hire professional traders to manage portfolios, and access market analytics tools." },
      { type:"list", label:"Available services", items:[
        "Copy Trading -- automated mirroring of verified signal providers with configurable risk limits",
        "Trader Hire -- delegation of portfolio management to vetted professional traders",
        "Analytics Dashboard -- Sharpe ratio, drawdown analysis, and side-by-side trade insights",
        "Paper Trading -- simulated trading environment for practice (no real funds involved)",
        "Notifications & Alerts -- real-time alerts for price movements and portfolio events",
      ]},
      { type:"callout", icon:"📊", text:"Past performance of any signal provider or hired trader is not indicative of future results. All trading involves risk and you may lose some or all of your invested capital." },
    ],
  },
  {
    id: "fees",
    icon: "💳",
    color: C.blue,
    title: "Fees & Payments",
    content: [
      { type:"p", text:"Digitalbrutalnft charges fees for certain services. All fees are displayed before you commit to a transaction and are subject to change with 30 days' notice." },
      { type:"list", label:"Fee structure", items:[
        "Copy Trading -- no platform subscription fee; signal providers may charge a performance fee of 10-30% of profits",
        "Trader Hire -- negotiated directly with the trader; Digitalbrutalnft charges a 5% intermediary fee on performance fees",
        "Deposits and withdrawals -- no platform fees; your bank or payment provider may charge their own fees",
        "Inactivity fee -- accounts with no activity for 12+ months may be charged GBP 5 per month",
        "Currency conversion -- a 0.5% spread applies to currency conversions",
      ]},
      { type:"p", text:"All fees are charged in the currency of your account. VAT or other applicable taxes will be added where required by law." },
    ],
  },
  {
    id: "risk",
    icon: "⚡",
    color: C.amber,
    title: "Risk Disclosure",
    content: [
      { type:"p", text:"Trading financial instruments carries a high level of risk and may not be suitable for all investors. You should carefully consider your investment objectives, level of experience, and risk appetite before using our Services." },
      { type:"list", label:"Key risks include", items:[
        "Market risk -- the value of investments can fall as well as rise",
        "Leverage risk -- some instruments allow trading on margin, magnifying both gains and losses",
        "Liquidity risk -- certain instruments may be difficult to sell at a fair price",
        "Counterparty risk -- third-party signal providers or hired traders may underperform or fail",
        "Technology risk -- platform outages or connectivity issues may prevent timely execution",
        "Regulatory risk -- changes in law or regulation may affect available instruments or your access",
      ]},
      { type:"callout", icon:"🔴", text:"You should never invest money you cannot afford to lose. If you are unsure whether trading is right for you, seek independent financial advice." },
    ],
  },
  {
    id: "prohibited",
    icon: "🚫",
    color: C.red,
    title: "Prohibited Conduct",
    content: [
      { type:"p", text:"You agree not to engage in any of the following activities when using Digitalbrutalnft. Violations may result in immediate account termination and referral to relevant authorities." },
      { type:"list", label:"Prohibited activities", items:[
        "Using the platform for money laundering, fraud, or any other illegal purpose",
        "Manipulating markets, engaging in wash trading, or disseminating false information",
        "Attempting to reverse-engineer, hack, scrape, or disrupt our platform infrastructure",
        "Using automated bots or scripts to access the Services without written authorisation",
        "Impersonating another person or entity, or misrepresenting your affiliation",
        "Uploading malicious code, viruses, or any content that could damage our systems",
        "Harvesting or collecting other users' data without their consent",
      ]},
    ],
  },
  {
    id: "intellectual-property",
    icon: "©️",
    color: C.purple,
    title: "Intellectual Property",
    content: [
      { type:"p", text:"All content, software, trademarks, logos, and materials on the Digitalbrutalnft platform are the exclusive property of Digitalbrutalnft Technologies Ltd or our licensors and are protected by applicable intellectual property laws." },
      { type:"list", label:"Permitted and restricted use", items:[
        "You may access and use the platform solely for your personal, non-commercial purposes",
        "You may not copy, reproduce, distribute, or create derivative works without express written consent",
        "You retain ownership of any content you submit, but grant Digitalbrutalnft a licence to use it to provide the Services",
        "Feedback or suggestions you provide may be used by Digitalbrutalnft without obligation to you",
      ]},
    ],
  },
  {
    id: "liability",
    icon: "⚖️",
    color: C.blue,
    title: "Limitation of Liability",
    content: [
      { type:"p", text:"To the fullest extent permitted by applicable law, Digitalbrutalnft and its directors, employees, and affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Services." },
      { type:"list", label:"Liability caps and exclusions", items:[
        "Our total aggregate liability to you shall not exceed the fees you paid to us in the 12 months preceding the claim",
        "We are not liable for losses caused by third-party signal providers or hired traders",
        "We are not liable for losses resulting from platform outages, force majeure events, or regulatory actions",
        "Nothing in these Terms excludes liability for death, personal injury caused by our negligence, or fraud",
      ]},
      { type:"callout", icon:"⚠️", text:"Some jurisdictions do not allow the exclusion or limitation of certain warranties or liabilities. In such cases, our liability will be limited to the maximum extent permitted by law." },
    ],
  },
  {
    id: "termination",
    icon: "🔚",
    color: C.accent,
    title: "Termination",
    content: [
      { type:"p", text:"Either party may terminate the agreement at any time. You may close your account through the Settings page. We may suspend or terminate your account immediately if we believe you have violated these Terms." },
      { type:"list", label:"Upon termination", items:[
        "All open copy positions will be closed at the prevailing market price",
        "Any hired trader mandates will be terminated with immediate effect",
        "Your remaining balance will be returned to your nominated payment method within 5 business days",
        "Your access to the platform and all data will be revoked",
        "Obligations that by their nature should survive termination (e.g. liability clauses) will remain in effect",
      ]},
    ],
  },
  {
    id: "governing-law",
    icon: "🏛️",
    color: C.textMid,
    title: "Governing Law & Disputes",
    content: [
      { type:"p", text:"These Terms are governed by and construed in accordance with the laws of England and Wales. Any dispute arising out of or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts of England and Wales." },
      { type:"p", text:"Before initiating formal proceedings, you agree to attempt to resolve any dispute informally by contacting us at legal@Digitalbrutalnft.io. We will make reasonable efforts to resolve complaints within 30 days." },
      { type:"callout", icon:"💬", text:"Complaints can also be escalated to the Financial Ombudsman Service (FOS) if unresolved. Visit financial-ombudsman.org.uk for details." },
    ],
  },
];

/* ─────────────────────────────────────────────
   GLOBAL CSS
───────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;0,9..144,700;0,9..144,800;0,9..144,900;1,9..144,400;1,9..144,500&family=Public+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400&family=IBM+Plex+Mono:wght@400;500;600&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
html { scroll-behavior: smooth; }
body {
  background: ${C.bg};
  color: ${C.text};
  font-family: 'Public Sans', sans-serif;
  -webkit-font-smoothing: antialiased;
  min-height: 100vh;
  overflow-x: hidden;
}
a { text-decoration: none; color: inherit; }

@keyframes fadeUp {
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes gridFade {
  from { opacity: 0; }
  to   { opacity: .3; }
}
@keyframes pulse {
  0%,100% { opacity:1; transform:scale(1); }
  50%     { opacity:.5; transform:scale(.9); }
}

/* ── Grid BG ── */
.grid-bg {
  position: fixed; inset: 0; pointer-events: none; z-index: 0;
  background-image:
    linear-gradient(${C.border} 1px, transparent 1px),
    linear-gradient(90deg, ${C.border} 1px, transparent 1px);
  background-size: 56px 56px;
  animation: gridFade 1.2s ease-out both;
  mask-image: radial-gradient(ellipse 70% 50% at 50% 0%, black 0%, transparent 100%);
}

/* ── Nav ── */
.tf-nav {
  height: 60px;
  display: flex; align-items: center; justify-content: space-between;
  padding: 0 clamp(16px, 4vw, 40px);
  background: ${C.bg}F2; backdrop-filter: blur(14px);
  border-bottom: 1px solid ${C.border};
  position: sticky; top: 0; z-index: 100;
}
.tf-nav-links { display:flex; align-items:center; gap:4px; }
.tf-nav-link {
  font-size:13px; font-weight:500; color:${C.textMid};
  padding:6px 12px; border-radius:7px;
  transition:color .15s, background .15s;
}
.tf-nav-link:hover { color:${C.text}; background:rgba(255,255,255,.04); }
.tf-nav-link.active { color:${C.text}; font-weight:600; }
@media(max-width:600px){ .tf-nav-links { display:none; } }

/* ── Buttons ── */
.btn-primary {
  display:inline-flex; align-items:center; justify-content:center; gap:8px;
  padding:8px 16px; background:${C.accent}; color:#06070B;
  border:none; border-radius:8px; font-size:12px; font-weight:700;
  font-family:'Public Sans',sans-serif; cursor:pointer; letter-spacing:.01em;
  transition:transform .18s, box-shadow .18s; text-decoration:none;
  position:relative; overflow:hidden;
}
.btn-primary::after {
  content:''; position:absolute; inset:0;
  background:linear-gradient(135deg,rgba(255,255,255,.16) 0%,transparent 60%);
  opacity:0; transition:opacity .2s;
}
.btn-primary:hover { transform:translateY(-2px); box-shadow:0 8px 28px ${C.accentGlow}; }
.btn-primary:hover::after { opacity:1; }

.btn-ghost {
  display:inline-flex; align-items:center; justify-content:center;
  padding:8px 16px; background:transparent; color:${C.textMid};
  border:1px solid ${C.border}; border-radius:8px; font-size:12px; font-weight:600;
  font-family:'Public Sans',sans-serif; cursor:pointer;
  transition:border-color .2s, color .2s; text-decoration:none;
}
.btn-ghost:hover { border-color:${C.borderHover}; color:${C.text}; }

/* ── Badge ── */
.badge-live {
  display:inline-flex; align-items:center; gap:5px;
  background:rgba(201,161,92,.10); border:1px solid rgba(201,161,92,.28);
  border-radius:6px; padding:3px 10px;
  font-family:'IBM Plex Mono',monospace; font-size:10px; color:${C.accent};
  letter-spacing:.04em; text-transform:uppercase;
}
.badge-live::before {
  content:''; width:5px; height:5px; border-radius:50%;
  background:${C.accent}; animation:pulse 1.5s ease-in-out infinite; flex-shrink:0;
}

/* ── TOC ── */
.toc-link {
  display:flex; align-items:center; gap:10px;
  padding:8px 12px; border-radius:8px;
  font-size:12px; font-weight:500; color:${C.textMid};
  cursor:pointer; transition:color .15s, background .15s;
  border:none; background:none; width:100%; text-align:left;
  font-family:'Public Sans',sans-serif; line-height:1.4;
}
.toc-link:hover { color:${C.text}; background:rgba(255,255,255,.04); }
.toc-link.active { color:${C.accent}; background:${C.accentDim}; font-weight:600; }
.toc-dot {
  width:5px; height:5px; border-radius:50%; flex-shrink:0;
  background:currentColor; opacity:.4; transition:opacity .15s, transform .15s;
}
.toc-link.active .toc-dot { opacity:1; transform:scale(1.5); }

/* ── Section card ── */
.policy-section {
  background:${C.surface};
  border:1px solid ${C.border};
  border-radius:10px;
  padding:clamp(24px,3vw,36px);
  margin-bottom:14px;
  scroll-margin-top:80px;
  transition:border-color .2s;
}
.policy-section:hover { border-color:${C.borderHover}; }

/* ── Callout ── */
.callout {
  display:flex; align-items:flex-start; gap:12px;
  border-radius:10px; padding:14px 16px; margin-top:16px;
}
.callout-default { background:${C.accentDim}; border:1px solid ${C.accent}25; }
.callout-warn    { background:rgba(214,137,16,.08); border:1px solid rgba(214,137,16,.2); }
.callout-danger  { background:rgba(192,57,43,.08); border:1px solid rgba(192,57,43,.2); }

/* ── List ── */
.policy-list { list-style:none; margin-top:10px; }
.policy-list li {
  display:flex; align-items:baseline; gap:10px;
  padding:7px 0; border-bottom:1px solid ${C.border}40;
  font-size:14px; color:${C.textMid}; line-height:1.65;
}
.policy-list li:last-child { border-bottom:none; }
.policy-list li::before {
  content:''; width:4px; height:4px; border-radius:50%;
  background:${C.accent}; flex-shrink:0; margin-top:9px;
}

/* ── Section number badge ── */
.sec-num {
  font-family:'IBM Plex Mono',monospace; font-size:10px; font-weight:500;
  color:${C.textDim}; letter-spacing:.06em;
  margin-bottom:4px; text-transform:uppercase;
}

/* ── Read progress ── */
.read-progress {
  position:fixed; top:0; left:0; height:2px; z-index:200;
  background:${C.accent}; transition:width .1s linear;
  box-shadow:0 0 10px ${C.accent};
}

/* ── Scrollbar ── */
::-webkit-scrollbar { width:5px; }
::-webkit-scrollbar-track { background:${C.bg}; }
::-webkit-scrollbar-thumb { background:${C.border}; border-radius:3px; }

/* ── Language switcher ── */
.lang-switcher { position:relative; display:inline-flex; align-items:center; }
.lang-btn {
  display:inline-flex; align-items:center; gap:6px;
  background:transparent; border:1px solid ${C.border};
  border-radius:8px; padding:6px 10px;
  font-size:12px; font-weight:600; color:${C.textMid};
  cursor:pointer; font-family:'Public Sans',sans-serif;
  transition:border-color .2s, color .2s, background .2s; white-space:nowrap;
}
.lang-btn:hover { border-color:${C.borderHover}; color:${C.text}; background:rgba(255,255,255,.04); }
.lang-dropdown {
  position:absolute; top:calc(100% + 6px); right:0;
  background:${C.surface}; border:1px solid ${C.border};
  border-radius:12px; padding:6px;
  min-width:180px; z-index:400;
  box-shadow:0 16px 40px rgba(0,0,0,.6);
  animation:fadeUp .2s ease both;
}
.lang-option {
  display:flex; align-items:center; gap:10px;
  padding:9px 12px; border-radius:8px;
  font-size:13px; color:${C.textMid};
  cursor:pointer; font-family:'Public Sans',sans-serif;
  transition:background .15s, color .15s;
}
.lang-option:hover { background:${C.surfaceAlt}; color:${C.text}; }
.lang-option.active { color:${C.accent}; background:${C.accentDim}; }

/* Hide Google's default toolbar */
.goog-te-banner-frame, #goog-gt-tt, .goog-te-balloon-frame { display:none !important; }
body { top:0 !important; }
.skiptranslate { display:none !important; }
`;

/* ─────────────────────────────────────────────
   LANGUAGE SWITCHER
   Reads "tf_lang" from localStorage (set on landing/login/about/policy)
   and auto-applies it — preserving the user's choice across pages.
───────────────────────────────────────────── */
const LANGUAGES = [
  { code:"en",    label:"English",   flag:"🇬🇧" },
  { code:"fr",    label:"Français",  flag:"🇫🇷" },
  { code:"es",    label:"Español",   flag:"🇪🇸" },
  { code:"de",    label:"Deutsch",   flag:"🇩🇪" },
  { code:"pt",    label:"Português", flag:"🇧🇷" },
  { code:"ar",    label:"العربية",   flag:"🇸🇦" },
  { code:"zh-CN", label:"中文",      flag:"🇨🇳" },
  { code:"ja",    label:"日本語",    flag:"🇯🇵" },
  { code:"ru",    label:"Русский",   flag:"🇷🇺" },
  { code:"hi",    label:"हिन्दी",    flag:"🇮🇳" },
  { code:"yo",    label:"Yoruba",    flag:"🇳🇬" },
  { code:"ha",    label:"Hausa",     flag:"🇳🇬" },
];

const initGoogleTranslate = () => {
  if (window._gtInitDone) return;
  window._gtInitDone = true;
  if (!document.getElementById("google_translate_element")) {
    const el = document.createElement("div");
    el.id = "google_translate_element";
    el.style.cssText = "display:none!important;position:absolute;left:-9999px;";
    document.body.appendChild(el);
  }
  window.googleTranslateElementInit = () => {
    new window.google.translate.TranslateElement({ pageLanguage:"en", autoDisplay:false }, "google_translate_element");
    const saved = localStorage.getItem("tf_lang");
    if (saved && saved !== "en") setTimeout(() => applyGoogleTranslate(saved), 800);
  };
  const script = document.createElement("script");
  script.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
  script.async = true;
  document.head.appendChild(script);
};

const applyGoogleTranslate = (langCode) => {
  if (langCode === "en") {
    document.cookie = "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    document.cookie = "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=" + window.location.hostname;
    window.location.reload();
    return;
  }
  const pair = "/en/" + langCode;
  document.cookie = "googtrans=" + pair + "; path=/";
  document.cookie = "googtrans=" + pair + "; path=/; domain=" + window.location.hostname;
  const sel = document.querySelector(".goog-te-combo");
  if (sel) { sel.value = langCode; sel.dispatchEvent(new Event("change")); }
  else window.location.reload();
};

const LanguageSwitcher = () => {
  const [open, setOpen]       = useState(false);
  const [current, setCurrent] = useState(() => localStorage.getItem("tf_lang") || "en");
  const ref                   = useRef(null);

  useEffect(() => { initGoogleTranslate(); }, []);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const select = (code) => {
    setCurrent(code);
    localStorage.setItem("tf_lang", code);
    setOpen(false);
    applyGoogleTranslate(code);
  };

  const active = LANGUAGES.find(l => l.code === current) || LANGUAGES[0];

  return (
    <div className="lang-switcher" ref={ref}>
      <button className="lang-btn" onClick={() => setOpen(o => !o)} title="Change language">
        <span style={{fontSize:"14px"}}>{active.flag}</span>
        <span>{active.label}</span>
        <span style={{fontSize:"9px", opacity:.6, marginLeft:"2px"}}>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="lang-dropdown">
          <div style={{fontSize:"10px", fontWeight:"600", color:C.textDim, letterSpacing:".08em", textTransform:"uppercase", padding:"4px 12px 8px"}}>
            Select Language
          </div>
          {LANGUAGES.map(l => (
            <div
              key={l.code}
              className={"lang-option" + (l.code === current ? " active" : "")}
              onClick={() => select(l.code)}
            >
              <span style={{fontSize:"16px"}}>{l.flag}</span>
              <span>{l.label}</span>
              {l.code === current && <span style={{marginLeft:"auto", fontSize:"11px"}}>✓</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────
   NAV
───────────────────────────────────────────── */
function PublicNav() {
  return (
    <nav className="tf-nav">
      <a href="/" style={{display:"flex", alignItems:"center"}}>
        <img src={logo} alt="Digitalbrutalnft" style={{height:"40px", width:"auto", filter:"invert(1) brightness(2)"}} />
      </a>
      <div className="tf-nav-links">
        <a href="/"      className="tf-nav-link">Home</a>
        <a href="/about" className="tf-nav-link">About</a>
        <a href="/news"  className="tf-nav-link">News</a>
      </div>
      <div style={{display:"flex", alignItems:"center", gap:"10px"}}>
        <LanguageSwitcher />
        <a href="/login"  className="btn-ghost">Log in</a>
        <a href="/signup" className="btn-primary">Sign up free →</a>
      </div>
    </nav>
  );
}

/* ─────────────────────────────────────────────
   SECTION RENDERER
───────────────────────────────────────────── */
function SectionBlock({ section, index }) {
  const calloutClass = (icon) => {
    if (icon === "⚠️" || icon === "🔴") return "callout callout-warn";
    if (icon === "🚫") return "callout callout-danger";
    return "callout callout-default";
  };

  return (
    <div id={section.id} className="policy-section">
      {/* Header */}
      <div style={{display:"flex", alignItems:"flex-start", gap:"14px", marginBottom:"20px"}}>
        <div style={{
          width:"44px", height:"44px", borderRadius:"12px", flexShrink:0,
          background:`${section.color}18`, border:`1px solid ${section.color}30`,
          display:"flex", alignItems:"center", justifyContent:"center", fontSize:"20px",
        }}>{section.icon}</div>
        <div>
          <div className="sec-num">Section {String(index + 1).padStart(2, "0")}</div>
          <h2 style={{fontFamily:"'Fraunces',serif", fontSize:"clamp(16px,2vw,19px)", fontWeight:"600", color:C.text, letterSpacing:"-0.3px", lineHeight:"1.2"}}>
            {section.title}
          </h2>
        </div>
      </div>

      {/* Content */}
      {section.content.map((block, i) => {
        if (block.type === "p") return (
          <p key={i} style={{fontSize:"14px", color:C.textMid, lineHeight:"1.8", marginBottom:"12px"}}>
            {block.text}
          </p>
        );

        if (block.type === "list") return (
          <div key={i} style={{marginBottom:"12px"}}>
            {block.label && (
              <div style={{fontSize:"10px", fontFamily:"'IBM Plex Mono',monospace", color:C.textDim, letterSpacing:".08em", textTransform:"uppercase", marginBottom:"6px", marginTop:"4px"}}>
                {block.label}
              </div>
            )}
            <ul className="policy-list">
              {block.items.map((item, j) => <li key={j}>{item}</li>)}
            </ul>
          </div>
        );

        if (block.type === "callout") return (
          <div key={i} className={calloutClass(block.icon)}>
            <span style={{fontSize:"18px", flexShrink:0, marginTop:"1px"}}>{block.icon}</span>
            <p style={{fontSize:"13px", color:C.textMid, lineHeight:"1.65"}}>{block.text}</p>
          </div>
        );

        return null;
      })}
    </div>
  );
}

/* ─────────────────────────────────────────────
   TABLE OF CONTENTS
───────────────────────────────────────────── */
function TableOfContents({ activeId }) {
  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior:"smooth", block:"start" });
  };
  return (
    <div style={{
      position:"sticky", top:"80px",
      background:C.surface, border:`1px solid ${C.border}`,
      borderRadius:"10px", padding:"16px",
      width:"224px", flexShrink:0, alignSelf:"flex-start",
    }}>
      <div style={{fontSize:"10px", fontFamily:"'IBM Plex Mono',monospace", color:C.textDim, letterSpacing:".08em", textTransform:"uppercase", marginBottom:"12px", paddingLeft:"12px"}}>
        Sections
      </div>
      {SECTIONS.map((s, i) => (
        <button key={s.id} className={`toc-link ${activeId === s.id ? "active" : ""}`} onClick={() => scrollTo(s.id)}>
          <span style={{fontFamily:"'IBM Plex Mono',monospace", fontSize:"10px", color:"inherit", opacity:.5, flexShrink:0, minWidth:"18px"}}>
            {String(i + 1).padStart(2, "0")}
          </span>
          <span className="toc-dot"/>
          {s.title}
        </button>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────
   READ PROGRESS
───────────────────────────────────────────── */
function ReadProgress() {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement;
      const total = el.scrollHeight - el.clientHeight;
      setPct(total > 0 ? (el.scrollTop / total) * 100 : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return <div className="read-progress" style={{width:`${pct}%`}}/>;
}

/* ─────────────────────────────────────────────
   ACTIVE SECTION TRACKER
───────────────────────────────────────────── */
function useActiveSection() {
  const [activeId, setActiveId] = useState(SECTIONS[0].id);
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => { entries.forEach(e => { if (e.isIntersecting) setActiveId(e.target.id); }); },
      { rootMargin: "-20% 0px -60% 0px" }
    );
    SECTIONS.forEach(s => { const el = document.getElementById(s.id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, []);
  return activeId;
}

/* ─────────────────────────────────────────────
   PAGE
───────────────────────────────────────────── */
const Terms = () => {
  const activeId = useActiveSection();

  return (
    <>
      <style>{CSS}</style>
      <ReadProgress/>
      <div className="grid-bg"/>
      <PublicNav/>

      {/* ── HERO ── */}
      <div style={{
        textAlign:"center",
        padding:"clamp(48px,7vw,88px) clamp(16px,6vw,48px) clamp(32px,4vw,48px)",
        position:"relative", zIndex:1,
        animation:"fadeUp .7s cubic-bezier(.22,1,.36,1) both",
      }}>
        <div style={{position:"absolute", width:"500px", height:"200px", borderRadius:"50%", background:C.accentGlow, filter:"blur(100px)", top:0, left:"50%", transform:"translateX(-50%)", pointerEvents:"none"}}/>
        <div style={{position:"relative", zIndex:1}}>
          <div className="badge-live" style={{marginBottom:"20px"}}>Legal</div>
          <h1 style={{
            fontFamily:"'Fraunces',serif",
            fontSize:"clamp(32px,5vw,56px)",
            fontWeight:"600", letterSpacing:"-2px", lineHeight:"1.05",
            color:C.text, marginBottom:"16px",
          }}>
            Terms of <span style={{fontStyle:"italic", color:C.accent}}>Service</span>
          </h1>
          <p style={{fontSize:"14px", color:C.textMid, lineHeight:"1.7", maxWidth:"500px", margin:"0 auto 28px"}}>
            Please read these Terms carefully before using Digitalbrutalnft. They set out your rights, our obligations, and the rules that apply to everyone on the platform.
          </p>

          {/* Meta strip */}
          <div style={{display:"flex", alignItems:"center", justifyContent:"center", gap:"32px", flexWrap:"wrap"}}>
            {[
              { label:"Effective date", val:"1 May 2025"      },
              { label:"Jurisdiction",   val:"England & Wales" },
              { label:"Sections",       val:`${SECTIONS.length} total` },
            ].map(({ label, val }) => (
              <div key={label} style={{textAlign:"center"}}>
                <div style={{fontSize:"10px", fontFamily:"'IBM Plex Mono',monospace", color:C.textDim, letterSpacing:".07em", textTransform:"uppercase", marginBottom:"3px"}}>{label}</div>
                <div style={{fontSize:"13px", color:C.textMid, fontWeight:"600"}}>{val}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── BODY ── */}
      <div style={{
        maxWidth:"1060px", margin:"0 auto",
        padding:"0 clamp(16px,4vw,40px) clamp(64px,8vw,100px)",
        display:"flex", gap:"28px", alignItems:"flex-start",
        position:"relative", zIndex:1,
      }}>

        {/* TOC sidebar (desktop only) */}
        <div style={{display:"none"}} className="terms-toc-desktop">
          <TableOfContents activeId={activeId}/>
        </div>
        <style>{`@media(min-width:860px){ .terms-toc-desktop{ display:block !important; } }`}</style>

        {/* Sections */}
        <div style={{flex:1, minWidth:0}}>
          {SECTIONS.map((s, i) => (
            <div key={s.id} style={{animation:`fadeUp .6s cubic-bezier(.22,1,.36,1) ${.04 + i * .05}s both`}}>
              <SectionBlock section={s} index={i}/>
            </div>
          ))}

          {/* Footer CTA */}
          <div style={{
            marginTop:"8px",
            background:C.surface, border:`1px solid ${C.border}`,
            borderRadius:"10px", padding:"28px 32px",
            display:"flex", alignItems:"center", gap:"20px", flexWrap:"wrap",
            boxShadow:`0 24px 48px rgba(0,0,0,.5)`,
            position:"relative", overflow:"hidden",
          }}>
            <div style={{position:"absolute", width:"300px", height:"150px", borderRadius:"50%", background:C.accentGlow, filter:"blur(70px)", bottom:"-60px", right:"0", pointerEvents:"none"}}/>
            <div style={{flex:1, minWidth:"200px", position:"relative", zIndex:1}}>
              <div style={{fontSize:"14px", fontWeight:"700", color:C.text, marginBottom:"4px", fontFamily:"'Fraunces',serif"}}>
                Questions about these Terms?
              </div>
              <div style={{fontSize:"13px", color:C.textMid}}>
                Our legal team is available at{" "}
                <a href="mailto:legal@Digitalbrutalnft.io" style={{color:C.accent, fontWeight:"600"}}>legal@Digitalbrutalnft.io</a>
              </div>
            </div>
            <div style={{display:"flex", gap:"10px", flexShrink:0, position:"relative", zIndex:1}}>
              <a href="/policy" className="btn-ghost" style={{padding:"11px 18px", fontSize:"13px"}}>Privacy Policy</a>
              <a href="/signup" className="btn-primary" style={{padding:"11px 22px", fontSize:"13px"}}>Get started →</a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Terms;